import { Box, CircularProgress } from '@mui/material';
import { useContext, useMemo, useState } from 'react';

import { UserContext } from '~/auth/userContext';
import AttachmentPreviewDialog from '~/components/AttachmentPreviewDialog';
import { ContractDetailPageHeader } from '~/components/contractReview/detailPage/ContractDetailPageHeader';
import { getPrimaryDocument } from '~/components/contractReview/detailPage/contractDocuments';
import { ContractTabContainer } from '~/components/contractReview/detailPage/ContractTabContainer';
import type { ContractReview } from '~/components/contractReview/types';
import { InvoiceDocumentViewer } from '~/components/invoices/detailPage/InvoiceDocumentViewer';
import { DetailTableHeightProvider } from '~/hooks/useDetailTableHeight';
import { CARD_SX, pageBandColor } from '~/theme';
import { canAccess, EDIT_CONTRACT } from '~/utils/role';

interface ContractDetailPageProps {
  contract: ContractReview | null;
  isLoading: boolean;
  /** `null` in a dialog, which closes from its own title bar. */
  onBack: (() => void) | null;
  backLabel?: string;
  /** Opens the contract edit dialog, which `_app.tsx` mounts app-wide. */
  onEdit: () => void;
  /** Opens that same dialog on its Invoice Review Rules section. */
  onAddRule: () => void;
  variant?: 'page' | 'preview';
}

/**
 * Full-page contract detail: the header band carries what the drawer's Overview tab
 * used to — vendor and term, what the contract costs and what it covers — over the
 * full-width detail tabs, so it is on screen from Rates and KPIs too.
 */
export function ContractDetailPage({
  contract,
  isLoading,
  onBack,
  backLabel,
  onEdit,
  onAddRule,
  variant = 'page',
}: ContractDetailPageProps) {
  const isPreview = variant === 'preview';
  const { userDetails } = useContext(UserContext);
  const canEdit = canAccess(userDetails.role, EDIT_CONTRACT);
  const [documentOpen, setDocumentOpen] = useState(false);

  // The agreement itself, which the header opens. Reading it meant finding it on
  // the Attachments tab before, among the ContractIQ reports written about it.
  const document = useMemo(() => getPrimaryDocument(contract), [contract]);
  const viewerDocuments = useMemo(
    () =>
      document?.attachmentUrl
        ? [
            {
              id: document.id,
              filePath: document.attachmentUrl,
              pageNumbers: [],
              label: 'Contract',
            },
          ]
        : [],
    [document],
  );

  return (
    <DetailTableHeightProvider>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100%"
        bgcolor={isPreview ? pageBandColor : 'grey.50'}
      >
        <ContractDetailPageHeader
          contract={contract}
          onBack={onBack}
          backLabel={backLabel}
          onOpenDocument={() => setDocumentOpen(true)}
          hasDocument={document !== undefined}
          onEdit={canEdit ? onEdit : null}
          compact={isPreview}
          footer={
            isPreview ? (
              <InvoiceDocumentViewer
                primaryDocuments={viewerDocuments}
                supportingDocuments={[]}
                isLoading={isLoading}
                emptyMessage="No document attached to this contract."
                sx={{
                  ...(document && { height: '75vh' }),
                  ...CARD_SX,
                  bgcolor: 'background.paper',
                }}
              />
            ) : null
          }
        />

        {/* The tabs need a contract, not a skeleton of one: every one of them reads its rate
            sets, cars or attachments. The header renders its own loading state above. */}
        <Box display={isPreview ? 'none' : 'flex'} flex={1} minHeight={0} minWidth={0}>
          {isPreview ? null : contract ? (
            <ContractTabContainer contract={contract} onAddRule={canEdit ? onAddRule : null} />
          ) : isLoading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flex={1}
              role="status"
              aria-label="Loading contract"
            >
              <CircularProgress />
            </Box>
          ) : null}
        </Box>

        {/* Mounted only while open: the dialog holds a loaded PDF. */}
        {documentOpen && document ? (
          <AttachmentPreviewDialog
            open
            filePath={document.attachmentUrl}
            onClose={() => setDocumentOpen(false)}
          />
        ) : null}
      </Box>
    </DetailTableHeightProvider>
  );
}
